import { useCallback, useEffect, useMemo, useState } from "react";
import { apiPost } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import { formatOtpError, formatOtpExpiryHint } from "../utils/otpErrors";
import OtpInput from "./OtpInput";

const BLIND_REQUEST_MESSAGE =
  "If the details are correct, check your new email for verification instructions to finalize the change.";

export default function ChangeEmailModal({
  currentEmail = "",
  pendingEmail = null,
  onClose,
  onSuccess,
}) {
  const [step, setStep] = useState(pendingEmail ? "verify" : "request");
  const [currentInput, setCurrentInput] = useState(currentEmail);
  const [newEmail, setNewEmail] = useState(pendingEmail || "");
  const [otp, setOtp] = useState("");
  const [otpSessionId, setOtpSessionId] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [resendAvailableAt, setResendAvailableAt] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [otpExpiry, setOtpExpiry] = useState(null);
  const otpExpiryHint = useMemo(() => formatOtpExpiryHint(otpExpiry), [otpExpiry, resendTimer]);

  const startResendCooldown = useCallback((seconds = 30) => {
    setResendAvailableAt(Date.now() + seconds * 1000);
  }, []);

  useEffect(() => {
    if (!resendAvailableAt) {
      setResendTimer(0);
      return;
    }

    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000));
      setResendTimer(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 250);

    return () => clearInterval(interval);
  }, [resendAvailableAt]);

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setRequestMessage("");
    try {
      const { data } = await apiPost("/api/auth/change-email/request", {
        currentEmail: currentInput.trim(),
        newEmail: newEmail.trim(),
      });
      setOtpSessionId(data?.otpSessionId || "");
      setOtpExpiry(data?.clientExpiry || data?.expiry || null);
      setRequestMessage(data?.message || BLIND_REQUEST_MESSAGE);
      setStep("verify");
      startResendCooldown(30);
    } catch (err) {
      setError(formatOtpError(parseApiError(err, "Failed to process email change request")));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiPost("/api/auth/change-email/request", {
        currentEmail: currentInput.trim() || currentEmail,
        newEmail: newEmail.trim(),
      });
      setOtpSessionId(data?.otpSessionId || "");
      setOtpExpiry(data?.clientExpiry || data?.expiry || null);
      setRequestMessage(data?.message || BLIND_REQUEST_MESSAGE);
      startResendCooldown(30);
    } catch (err) {
      setError(formatOtpError(parseApiError(err, "Failed to resend verification instructions")));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.trim().length !== 6) {
      setError("Enter the 6-digit verification code.");
      return;
    }
    if (!otpSessionId) {
      setError("No active verification session found. Request a new code and check your inbox.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await apiPost("/api/auth/change-email/verify", {
        newEmail: newEmail.trim(),
        otp: otp.trim(),
        otpSessionId,
      });
      onSuccess?.(data?.user);
      onClose?.();
    } catch (err) {
      setError(formatOtpError(parseApiError(err, "Verification failed")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-email-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="change-email-title" className="page-subtitle" style={{ margin: 0 }}>
            {step === "verify" ? "Check your new email" : "Change email"}
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="modal-lead">
          {step === "verify"
            ? requestMessage || BLIND_REQUEST_MESSAGE
            : "For your security, we send a confirmation code to your new email address. Your current email stays active until verification succeeds."}
        </p>

        {step === "request" ? (
          <form className="auth-form" onSubmit={handleRequest}>
            <label className="field-label">
              Current email
              <input
                className="input"
                type="email"
                value={currentInput}
                readOnly
              />
            </label>
            <label className="field-label">
              New email
              <input
                className="input"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
              />
            </label>
            <p className="auth-footnote" style={{ marginTop: 0, textAlign: "left" }}>
              You will only be able to finalize the change after confirming ownership of the new address.
            </p>
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? "Processing..." : "Send confirmation code"}
            </button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleVerify}>
            <label className="field-label">
              New email
              <input className="input" type="email" value={newEmail} readOnly />
            </label>
            <div className="field-label">
              Verification code
              <OtpInput value={otp} onChange={setOtp} disabled={loading} autoFocus />
              {otpExpiryHint ? <span className="otp-expiry-hint">{otpExpiryHint}</span> : null}
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading || otp.length !== 6}>
              {loading ? "Verifying..." : "Confirm new email"}
            </button>
            <div className="auth-actions">
              <button type="button" className="auth-link-btn" onClick={() => setStep("request")}>
                Edit emails
              </button>
              <button
                type="button"
                className={`auth-link-btn ${loading || resendTimer > 0 ? "is-disabled" : ""}`}
                onClick={handleResend}
                disabled={loading || resendTimer > 0}
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend code"}
              </button>
            </div>
            <p className="auth-footnote" style={{ marginTop: 0, textAlign: "left" }}>
              Didn't receive a code? Wait a few minutes and check spam. If nothing arrives, the address may already be linked to another account — try signing in with that email instead.
            </p>
          </form>
        )}

        {error ? <div className="progress-error" style={{ marginTop: 12 }}>{error}</div> : null}
      </div>
    </div>
  );
}
